/**
 * Waves-Yo: Dawn of Titans
 * Application Entry Point
 *
 * Responsibilities:
 *   1. Register Service Worker and handle update notifications
 *   2. Gate game initialization on GDPR/CCPA consent (initConsent)
 *   3. Provide the game bootstrap hook (called on consent acceptance)
 */

import { initConsent } from './privacy/consent';

// ─── Service Worker Registration ─────────────────────────────────────────────

async function registerServiceWorker(): Promise<void> {
  if (!('serviceWorker' in navigator)) return;

  try {
    const registration = await navigator.serviceWorker.register('./sw.js', {
      // Scope relative to the page root — works on any static host subdirectory
      scope: './',
    });

    console.log('[SW] Registered. Scope:', registration.scope);

    // Listen for updates: when a new SW is waiting, show the update banner
    registration.addEventListener('updatefound', () => {
      const newWorker = registration.installing;
      newWorker?.addEventListener('statechange', () => {
        if (
          newWorker.state === 'installed' &&
          navigator.serviceWorker.controller
        ) {
          // A new version is ready — show the non-intrusive update banner
          showUpdateBanner(registration);
        }
      });
    });

    // Also catch SW_UPDATED messages from the SW's stale-while-revalidate logic
    navigator.serviceWorker.addEventListener('message', (event) => {
      if (event.data?.type === 'SW_UPDATED') {
        showUpdateBanner(registration);
      }
    });
  } catch (err) {
    // SW registration failure is non-fatal — the game runs without offline support
    console.warn('[SW] Registration failed:', err);
  }
}

function showUpdateBanner(registration: ServiceWorkerRegistration): void {
  const banner = document.getElementById('update-banner');
  const reloadBtn = document.getElementById('update-reload-btn');
  if (!banner) return;

  banner.classList.add('visible');

  reloadBtn?.addEventListener(
    'click',
    () => {
      // Tell the waiting SW to skip waiting and take control, then reload
      registration.waiting?.postMessage({ type: 'SKIP_WAITING' });
      navigator.serviceWorker.addEventListener('controllerchange', () => {
        window.location.reload();
      });
    },
    { once: true }
  );
}

// ─── Game Bootstrap (called after consent accepted) ──────────────────────────

/**
 * This is the Phase 1 placeholder.
 * Phase 2 will replace this with:
 *   - WebGL context initialization
 *   - ECS entity system setup
 *   - Flow field + spatial hash grid init
 *   - Main game loop start
 */
function initGame(): void {
  console.log('[Game] Consent accepted — initializing game...');

  // Phase 1: Placeholder canvas setup
  const canvas = document.getElementById('game-canvas') as HTMLCanvasElement | null;
  if (!canvas) return;

  // Size canvas to fill the viewport
  resizeCanvas(canvas);
  window.addEventListener('resize', () => resizeCanvas(canvas));

  // Placeholder render — will be replaced by WebGL renderer in Phase 2
  const ctx = canvas.getContext('2d');
  if (!ctx) {
    console.warn('[Game] Canvas 2D context unavailable.');
    return;
  }

  drawPlaceholderBattlefield(ctx, canvas);
}

function resizeCanvas(canvas: HTMLCanvasElement): void {
  const dpr = window.devicePixelRatio || 1;
  canvas.width = window.innerWidth * dpr;
  canvas.height = window.innerHeight * dpr;
  canvas.style.width = `${window.innerWidth}px`;
  canvas.style.height = `${window.innerHeight}px`;
}

function drawPlaceholderBattlefield(
  ctx: CanvasRenderingContext2D,
  canvas: HTMLCanvasElement
): void {
  const { width, height } = canvas;
  const dpr = window.devicePixelRatio || 1;
  ctx.scale(dpr, dpr);

  const w = width / dpr;
  const h = height / dpr;

  // Background
  ctx.fillStyle = '#020617';
  ctx.fillRect(0, 0, w, h);

  // Battlefield ground gradient
  const groundGrad = ctx.createLinearGradient(0, h * 0.5, 0, h);
  groundGrad.addColorStop(0, '#1e293b');
  groundGrad.addColorStop(1, '#0f172a');
  ctx.fillStyle = groundGrad;
  ctx.fillRect(0, h * 0.5, w, h * 0.5);

  // Horizon line
  ctx.strokeStyle = '#334155';
  ctx.lineWidth = 1;
  ctx.beginPath();
  ctx.moveTo(0, h * 0.5);
  ctx.lineTo(w, h * 0.5);
  ctx.stroke();

  // Title / placeholder text
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';

  ctx.fillStyle = '#f59e0b';
  ctx.font = `bold ${Math.floor(Math.min(w, h) * 0.06)}px Georgia, serif`;
  ctx.fillText('WAVES-YO: DAWN OF TITANS', w / 2, h * 0.35);

  ctx.fillStyle = '#06b6d4';
  ctx.font = `${Math.floor(Math.min(w, h) * 0.03)}px system-ui, sans-serif`;
  ctx.fillText('Phase 1 Complete — Game Engine Initializing in Phase 2', w / 2, h * 0.44);

  ctx.fillStyle = '#475569';
  ctx.font = `${Math.floor(Math.min(w, h) * 0.022)}px system-ui, sans-serif`;
  ctx.fillText('⚔️ Hold the Line ⚔️', w / 2, h * 0.7);
}

// ─── Boot Sequence ────────────────────────────────────────────────────────────

async function boot(): Promise<void> {
  // Register SW in parallel (non-blocking for game init)
  void registerServiceWorker();

  // Wire consent modal — calls initGame() once user accepts
  initConsent(() => {
    initGame();
  });
}

// Start
void boot();
